/**
 * Compatibility projection between the raw customer address list and the checkout selection.
 *
 * `auth.user.enrichment.addresses` is server data. `checkoutAddress.selectedAddressId` is the
 * ordering-session selection. Existing consumers read `Address.isDefault`; that flag is derived
 * here from the selection and is never stored. Any `isDefault` present on the input is ignored.
 */

import type { Address } from "@/models/user"

function sameId(address: Address, selectedAddressId: string | null): boolean {
  if (!selectedAddressId) return false
  return String(address.address_id) === selectedAddressId
}

/** Immutable map: every element gets `isDefault` from the selection, overwriting any input value. */
export function projectCheckoutAddresses(
  addresses: ReadonlyArray<Address>,
  selectedAddressId: string | null
): Address[] {
  return addresses.map((address) => ({
    ...address,
    isDefault: sameId(address, selectedAddressId),
  }))
}

/** The selected address when it exists in the current list; null when unselected or unresolved. */
export function resolveCheckoutAddress(
  addresses: ReadonlyArray<Address>,
  selectedAddressId: string | null
): Address | null {
  if (!selectedAddressId) return null
  const match = addresses.find((address) => sameId(address, selectedAddressId))
  return match ? { ...match, isDefault: true } : null
}
